import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import Compressor from "compressorjs";
import api from "../../services/api";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

import { i18n } from "../../translate/i18n";
import MessageVariablesPicker from "../MessageVariablesPicker";

import {
  Box,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  AddCircle,
  Delete,
  FormatListBulleted,
  Info,
  SmartButton,
  TouchApp,
} from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexWrap: "wrap" },
  textField: { marginRight: theme.spacing(1), flex: 1 },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const FlowBuilderInteractiveModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const messageRef = useRef(null);
  const footerRef = useRef(null);

  const [activeModal, setActiveModal] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [interactiveType, setInteractiveType] = useState("button");
  const [message, setMessage] = useState("");
  const [footer, setFooter] = useState("");
  const [headerImage, setHeaderImage] = useState("");
  const [headerImagePreview, setHeaderImagePreview] = useState("");
  const [headerImageMedias, setHeaderImageMedias] = useState([]);
  const [headerImageLoading, setHeaderImageLoading] = useState(false);
  const [listButtonText, setListButtonText] = useState("Ver opções");
  const [arrayOption, setArrayOption] = useState([]);
  const [labels, setLabels] = useState({ title: "Mensagem Interativa", btn: "Adicionar" });

  const maxOptions = interactiveType === "list" ? 10 : 3;

  useEffect(() => {
    if (open === "edit" || open === "create") {
      const isEdit = open === "edit";
      setLabels({ title: isEdit ? "Editar Mensagem Interativa" : "Nova Mensagem Interativa", btn: isEdit ? "Salvar" : "Adicionar" });
      const savedType = isEdit ? (data.data.interactiveType || "button") : "button";
      setInteractiveType(savedType);
      setMessage(isEdit ? (data.data.message || "") : "");
      setFooter(isEdit ? (data.data.footer || "") : "");
      setHeaderImage(isEdit ? (data.data.headerImage || "") : "");
      setHeaderImagePreview(isEdit && data.data.headerImage ? process.env.REACT_APP_BACKEND_URL + '/public/' + data.data.headerImage : "");
      setHeaderImageMedias([]);
      setListButtonText(isEdit ? (data.data.listButtonText || "Ver opções") : "Ver opções");
      const opts = isEdit ? (data.data.arrayOption || []) : [];
      setArrayOption(savedType === "button" ? opts.slice(0, 3) : opts.slice(0, 10));
      setTabIndex(0);
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  const handleClose = () => { close(null); setActiveModal(false); };

  const handleTypeChange = (_, newType) => {
    if (!newType) return;
    setInteractiveType(newType);
    if (newType === "button") {
      setArrayOption(prev => prev.slice(0, 3));
    }
  };

  const handleSave = async () => {
    if (!message.trim()) { toast.error("O corpo da mensagem é obrigatório!"); return; }
    if (arrayOption.length === 0) { toast.error("Adicione pelo menos uma opção!"); return; }
    if (interactiveType === "button" && arrayOption.length > 3) { toast.error("Botões: máximo 3 opções!"); return; }

    let finalHeaderImage = headerImage;
    if (headerImageMedias.length > 0) {
      setHeaderImageLoading(true);
      try {
        const formData = new FormData();
        formData.append("fromMe", true);
        const file = headerImageMedias[0];
        await new Promise((resolve, reject) => {
          if (file.type.split("/")[0] === "image") {
            new Compressor(file, {
              quality: 0.7,
              success(compressed) { formData.append("medias", compressed); formData.append("body", compressed.name); resolve(); },
              error(err) { reject(err); }
            });
          } else { formData.append("medias", file); formData.append("body", file.name); resolve(); }
        });
        const res = await api.post("/flowbuilder/img", formData);
        finalHeaderImage = res.data.name;
      } catch (err) { toast.error("Erro ao enviar imagem"); setHeaderImageLoading(false); return; }
      setHeaderImageLoading(false);
    }

    const payload = {
      message,
      footer,
      headerImage: finalHeaderImage,
      interactiveType,
      listButtonText: interactiveType === "list" ? (listButtonText || "Ver opções") : "",
      arrayOption,
    };

    if (open === "edit") { handleClose(); onUpdate({ ...data, data: payload }); }
    else { handleClose(); onSave(payload); }
  };

  const handleHeaderImageChange = (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    if (e.target.files[0].size > 2000000) { toast.error("Arquivo muito grande! Máximo 2MB"); return; }
    setHeaderImagePreview(URL.createObjectURL(e.target.files[0]));
    setHeaderImageMedias(Array.from(e.target.files));
  };

  const handleRemoveHeaderImage = () => { setHeaderImage(""); setHeaderImagePreview(""); setHeaderImageMedias([]); };

  const addOption = () => {
    if (arrayOption.length >= maxOptions) { toast.warning(`Limite: ${maxOptions} ${interactiveType === "list" ? "opções" : "botões"}!`); return; }
    setArrayOption(old => [...old, { number: old.length + 1, value: "" }]);
  };

  const removeOption = (number) => {
    setArrayOption(old => old.filter(i => i.number !== number).map((i, idx) => ({ ...i, number: idx + 1 })));
  };

  const updateOptionValue = (index, value) => {
    setArrayOption(old => { const a = [...old]; a[index] = { ...a[index], value }; return a; });
  };

  const handleAddVariable = (value) => {
    const input = messageRef.current?.querySelector("textarea");
    if (input) {
      const s = input.selectionStart, e = input.selectionEnd;
      setMessage(message.substring(0, s) + value + message.substring(e));
      setTimeout(() => { input.selectionStart = input.selectionEnd = s + value.length; input.focus(); }, 50);
    } else setMessage(prev => prev + value);
  };

  const Preview = () => (
    <Box sx={{ backgroundColor: "#FFF8E1", border: "1px solid #FFE082", borderRadius: "8px", padding: "12px", mt: 2 }}>
      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        <TouchApp sx={{ color: "#9C27B0", fontSize: 18 }} />
        <Typography style={{ fontWeight: 600, fontSize: "13px", color: "#333" }}>Pré-visualização</Typography>
      </Stack>
      <Box sx={{ backgroundColor: "#e1ffc7", borderRadius: "8px", padding: "10px 14px", maxWidth: "300px" }}>
        {headerImagePreview && (
          <img src={headerImagePreview} alt="Header"
            style={{ width: "100%", borderRadius: "6px", marginBottom: "8px", maxHeight: "120px", objectFit: "cover" }}
            onError={e => { e.target.style.display = "none"; }} />
        )}
        <Typography style={{ fontSize: "13px", color: "#333" }}>{message || "Corpo da mensagem..."}</Typography>
        {footer && <Typography style={{ fontSize: "11px", color: "#888", marginTop: "4px" }}>{footer}</Typography>}
      </Box>
      {interactiveType === "button" && arrayOption.length > 0 && (
        <Stack gap={0.5} mt={1} maxWidth="300px">
          {arrayOption.map(opt => (
            <Box key={opt.number} sx={{ backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: "6px", padding: "6px 12px", textAlign: "center" }}>
              <Typography style={{ fontSize: "12px", color: "#00a5f4", fontWeight: 600 }}>{opt.value || `Botão ${opt.number}`}</Typography>
            </Box>
          ))}
        </Stack>
      )}
      {interactiveType === "list" && (
        <Box sx={{ mt: 1, maxWidth: "300px" }}>
          <Box sx={{ backgroundColor: "#fff", border: "1px solid #ddd", borderRadius: "6px", padding: "8px 12px", textAlign: "center", mb: 0.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5}>
              <FormatListBulleted sx={{ fontSize: 14, color: "#00a5f4" }} />
              <Typography style={{ fontSize: "12px", color: "#00a5f4", fontWeight: 600 }}>{listButtonText || "Ver opções"}</Typography>
            </Stack>
          </Box>
          {arrayOption.length > 0 && (
            <Box sx={{ backgroundColor: "#fff", border: "1px solid #eee", borderRadius: "6px", padding: "4px 8px" }}>
              {arrayOption.map(opt => (
                <Typography key={opt.number} style={{ fontSize: "11px", color: "#333", padding: "3px 0", borderBottom: "1px solid #f0f0f0" }}>
                  {opt.value || `Opção ${opt.number}`}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <div className={classes.root}>
      <Dialog open={activeModal} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={1}>
            <TouchApp sx={{ color: "#9C27B0" }} />
            <span>{labels.title}</span>
          </Stack>
        </DialogTitle>

        <Box sx={{ px: 2, pt: 1 }}>
          <Typography variant="body2" style={{ marginBottom: 6, fontWeight: 600, color: "#444" }}>Tipo de mensagem</Typography>
          <ToggleButtonGroup value={interactiveType} exclusive onChange={handleTypeChange} size="small" sx={{ mb: 1 }}>
            <ToggleButton value="button" sx={{ gap: 1, fontSize: 12, px: 2 }}>
              <SmartButton sx={{ fontSize: 16 }} /> Botões (máx. 3)
            </ToggleButton>
            <ToggleButton value="list" sx={{ gap: 1, fontSize: 12, px: 2 }}>
              <FormatListBulleted sx={{ fontSize: 16 }} /> Lista (máx. 10)
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ backgroundColor: interactiveType === "list" ? "#E8F5E9" : "#E3F2FD", borderRadius: "6px", padding: "8px 12px", display: "flex", alignItems: "center", gap: "8px", mb: 1 }}>
            <Info sx={{ color: interactiveType === "list" ? "#2E7D32" : "#1976D2", fontSize: 18 }} />
            <Typography style={{ fontSize: "11px", color: "#333" }}>
              {interactiveType === "list"
                ? "Lista interativa: botão abre menu com até 10 opções. Ideal para mais de 3 escolhas."
                : "Botões clicáveis: até 3 botões no final da mensagem. Funciona no WhatsApp Baileys."}
            </Typography>
          </Box>
        </Box>

        <DialogContent dividers>
          <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} indicatorColor="primary" textColor="primary">
            <Tab label="MENSAGEM" />
            <Tab label={`${interactiveType === "list" ? "OPÇÕES" : "BOTÕES"} (${arrayOption.length}/${maxOptions})`} />
          </Tabs>

          {tabIndex === 0 && (
            <Stack gap={2} mt={2}>
              <MessageVariablesPicker onClick={handleAddVariable} disabled={false} />
              <div ref={messageRef}>
                <TextField label="Corpo da mensagem" multiline rows={4} variant="outlined" value={message}
                  onChange={e => setMessage(e.target.value)} fullWidth required />
              </div>
              <TextField label="Rodapé (opcional)" variant="outlined" value={footer}
                onChange={e => setFooter(e.target.value)} fullWidth size="small" />
              {interactiveType === "list" && (
                <TextField label='Texto do botão da lista (ex: "Ver opções")' variant="outlined"
                  value={listButtonText} onChange={e => setListButtonText(e.target.value)}
                  fullWidth size="small" inputProps={{ maxLength: 60 }}
                  helperText={`${(listButtonText || "").length}/60`} />
              )}
              {interactiveType === "button" && (
                <Box sx={{ border: "1px dashed #ccc", borderRadius: "6px", padding: "12px", textAlign: "center" }}>
                  <Typography style={{ fontSize: "13px", color: "#555", marginBottom: "8px" }}>Imagem de cabeçalho (opcional)</Typography>
                  {headerImagePreview && (
                    <Box sx={{ mb: 1, position: "relative", display: "inline-block" }}>
                      <img src={headerImagePreview} alt="Preview"
                        style={{ maxWidth: "200px", maxHeight: "100px", borderRadius: "6px", objectFit: "cover" }} />
                      <IconButton size="small" onClick={handleRemoveHeaderImage}
                        sx={{ position: "absolute", top: -8, right: -8, backgroundColor: "#fff", boxShadow: 1 }}>
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  )}
                  {!headerImageLoading && (
                    <Button variant="outlined" component="label" size="small">
                      {headerImagePreview ? "Trocar imagem" : "Enviar imagem"}
                      <input type="file" accept="image/png,image/jpg,image/jpeg" hidden onChange={handleHeaderImageChange} />
                    </Button>
                  )}
                  {headerImageLoading && <CircularProgress size={24} />}
                </Box>
              )}
              <Preview />
            </Stack>
          )}

          {tabIndex === 1 && (
            <Stack gap={2} mt={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography style={{ fontWeight: 500 }}>
                  {interactiveType === "list" ? `Opções da lista (${arrayOption.length}/${maxOptions})` : `Botões (${arrayOption.length}/${maxOptions})`}
                </Typography>
                <Button onClick={addOption} color="primary" variant="contained" size="small" startIcon={<AddCircle />} disabled={arrayOption.length >= maxOptions}>
                  Adicionar
                </Button>
              </Stack>
              {arrayOption.length === 0 && (
                <Box sx={{ backgroundColor: "#FFF3E0", border: "1px solid #FFE0B2", borderRadius: "6px", padding: "12px" }}>
                  <Typography style={{ fontSize: "13px", color: "#E65100" }}>⚠ Adicione pelo menos uma opção.</Typography>
                </Box>
              )}
              {arrayOption.map((item, index) => (
                <Stack key={item.number} direction="row" alignItems="center" gap={1}>
                  <Chip label={item.number} size="small"
                    sx={{ backgroundColor: interactiveType === "list" ? "#1976D2" : "#388E3C", color: "#fff", fontWeight: 600, minWidth: "28px" }} />
                  <TextField
                    placeholder={interactiveType === "list" ? `Opção ${item.number} (ex: Financeiro)` : `Botão ${item.number}`}
                    variant="outlined" size="small" value={item.value}
                    onChange={e => updateOptionValue(index, e.target.value)}
                    fullWidth inputProps={{ maxLength: 60 }}
                    helperText={`${(item.value || "").length}/${60}`} />
                  <IconButton size="small" onClick={() => removeOption(item.number)}><Delete fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Preview />
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleSave}>{labels.btn}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderInteractiveModal;
